import { ref, get, push, set } from 'firebase/database';
import { db } from './firebaseConfig';
import trackingService from './trackingService';

/**
 * Workshop Data Service
 * Handles Firebase Realtime DB queries for workshops and registrations
 */

/**
 * Fetch workshop data by ID
 * @param {string} workshopId - Workshop ID
 * @returns {Promise<Object>} Workshop data
 */
export const fetchWorkshop = async (workshopId) => {
  try {
    const workshopRef = ref(db, `/testmeta/workshops/${workshopId}`);
    const snapshot = await get(workshopRef);

    if (!snapshot.exists()) {
      throw new Error('Workshop not found');
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error fetching workshop:', error);
    throw error;
  }
};

/**
 * Fetch location data by location ID
 * @param {string} locationId - Location ID (slug)
 * @returns {Promise<Object>} Location data with address and maps URL
 */
export const fetchLocation = async (locationId) => {
  try {
    const locationRef = ref(db, `/testmeta/locations/${locationId}`);
    const snapshot = await get(locationRef);

    if (!snapshot.exists()) {
      throw new Error('Location not found');
    }

    return snapshot.val();
  } catch (error) {
    console.error('Error fetching location:', error);
    throw error;
  }
};

/**
 * Fetch online URL for workshop (if online: true)
 * @param {string} userId - User ID
 * @param {string} workshopId - Workshop ID
 * @returns {Promise<string>} Online URL (Zoom/Zoho link)
 */
export const fetchWorkshopOnlineUrl = async (userId, workshopId) => {
  try {
    const urlRef = ref(db, `/tests_data/${userId}/workshops/${workshopId}/url`);
    const snapshot = await get(urlRef);

    return snapshot.exists() ? snapshot.val() : null;
  } catch (error) {
    console.error('Error fetching workshop online URL:', error);
    return null;
  }
};

/**
 * Save workshop registration to forms/workshop path
 * Follows same pattern as webinar/form submissions
 * @param {string} workshopId - Workshop ID
 * @param {Object} registrationData - Registration form data (name, email, phone, message, influencerId, flags)
 * @returns {Promise<string>} Registration ID
 */
export const saveWorkshopRegistration = async (workshopId, registrationData) => {
  try {
    const workshopsRef = ref(db, `forms/workshop`);
    const newRegistrationRef = push(workshopsRef);

    const dataToSave = {
      ...registrationData,
      workshopId,
      formType: 'workshop',
      submittedAt: new Date().toISOString(),
      userId: trackingService.getUserId(),
      influencerId: trackingService.getInfluencerId() || registrationData.influencerId,
      metadata: {
        url: typeof window !== 'undefined' ? window.location.href : '',
        userAgent: typeof window !== 'undefined' ? navigator.userAgent : '',
      },
    };

    await set(newRegistrationRef, dataToSave);

    console.log('✅ Workshop registration saved:', newRegistrationRef.key);
    return newRegistrationRef.key;
  } catch (error) {
    console.error('❌ Error saving registration:', error);
    throw error;
  }
};

/**
 * Send confirmation email via cloud function
 * @param {string} workshopId - Workshop ID
 * @param {Object} emailData - Email payload
 * @returns {Promise<Object>} Response from cloud function
 */
export const sendConfirmationEmail = async (workshopId, emailData) => {
  try {
    const response = await fetch('/api/sendWorkshopConfirmationEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        workshopId,
        ...emailData,
      }),
    });

    if (!response.ok) {
      throw new Error(`Email service error: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('❌ Error sending confirmation email:', error);
    // Don't throw - registration succeeded even if email fails
    return { success: false, error: error.message };
  }
};
